import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ownerSelector } from "../../store/store";
import { fetchUserOwnersWithWorkspaces, resolveDashboardScope } from "../../utils";
import {
  getStoredAiContentOwnerId,
  setStoredAiContentOwnerId,
} from "../../utils/aiContent";
import { Select } from "../../components/ui";

type OwnerOption = {
  ownerId: string;
  name: string;
};

export type AiContentOutletContext = {
  ownerId: string;
  userId?: string;
  workspaceId?: string;
  ownerName: string;
  refreshAccounts: () => Promise<void>;
};

const AiContentLayout = () => {
  const navigate = useNavigate();
  const { owner, currentUser, currentWorkspace, linkedOwners } =
    useSelector(ownerSelector);
  const [accounts, setAccounts] = useState<OwnerOption[]>([]);
  const [ownerId, setOwnerId] = useState(() => getStoredAiContentOwnerId());

  const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
    currentUser?.userId,
    currentWorkspace?._id
  );

  const refreshAccounts = async () => {
    try {
      const result = await fetchUserOwnersWithWorkspaces(
        1,
        100,
        "",
        resolvedUserId,
        resolvedWorkspaceId,
        owner?._id
      );
      const rows = Array.isArray(result?.owners)
        ? result.owners
        : Array.isArray(result)
          ? result
          : [];
      const mapped: OwnerOption[] = rows
        .map((row: any) => ({
          ownerId: String(row?.ownerId || row?._id || "").trim(),
          name: String(row?.name || row?.email || "LinkedIn account").trim(),
        }))
        .filter((r: OwnerOption) => r.ownerId);

      if (mapped.length === 0 && Array.isArray(linkedOwners)) {
        for (const lo of linkedOwners) {
          if (lo?.ownerId) {
            mapped.push({
              ownerId: String(lo.ownerId),
              name: String(lo.name || "LinkedIn account"),
            });
          }
        }
      }
      if (mapped.length === 0 && owner?._id) {
        mapped.push({
          ownerId: String(owner._id),
          name: String(owner.name || "LinkedIn account"),
        });
      }

      setAccounts(mapped);
      if (!ownerId && mapped[0]) {
        setOwnerId(mapped[0].ownerId);
        setStoredAiContentOwnerId(mapped[0].ownerId);
      } else if (
        ownerId &&
        mapped.length > 0 &&
        !mapped.some((m) => m.ownerId === ownerId)
      ) {
        setOwnerId(mapped[0].ownerId);
        setStoredAiContentOwnerId(mapped[0].ownerId);
      }
    } catch {
      if (owner?._id) {
        setAccounts([
          {
            ownerId: String(owner._id),
            name: String(owner.name || "LinkedIn account"),
          },
        ]);
        if (!ownerId) {
          setOwnerId(String(owner._id));
          setStoredAiContentOwnerId(String(owner._id));
        }
      }
    }
  };

  useEffect(() => {
    void refreshAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner?._id, resolvedUserId, resolvedWorkspaceId]);

  const ownerName = useMemo(() => {
    return (
      accounts.find((a) => a.ownerId === ownerId)?.name ||
      owner?.name ||
      "LinkedIn account"
    );
  }, [accounts, ownerId, owner?.name]);

  const tabs = [
    { to: "/ai-content/strategy", label: "Content Strategy" },
    { to: "/ai-content/plan", label: "30-Day Plan" },
    { to: "/ai-content/scheduled", label: "Scheduled Posts" },
    { to: "/ai-content/history", label: "Post History" },
  ];

  const outletContext: AiContentOutletContext = {
    ownerId,
    userId: resolvedUserId,
    workspaceId: resolvedWorkspaceId,
    ownerName,
    refreshAccounts,
  };

  return (
    <section className="font-inter noScroll flex h-full min-h-0 w-full flex-col overflow-y-auto overflow-x-hidden bg-paper">
      <div className="border-b border-line bg-white px-4 py-6 sm:px-9 sm:py-6">
        <div className="mx-auto max-w-[1600px]">
          <header>
            <h1 className="text-[27px] font-semibold tracking-tight text-flow-config-text">
              AI Poster
            </h1>
            <p className="mt-1 text-sm text-muted">
              Plan posts with AI, approve them, then innLead publishes on schedule.
            </p>
          </header>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Select
              variant="compact"
              size="sm"
              ariaLabel="LinkedIn account"
              value={ownerId}
              onChange={(value) => {
                setOwnerId(value);
                setStoredAiContentOwnerId(value);
              }}
              options={
                accounts.length
                  ? accounts.map((a) => ({ value: a.ownerId, label: a.name }))
                  : [{ value: "", label: "No accounts" }]
              }
              className="h-10 w-[220px] max-w-full !border-line font-semibold text-flow-config-text"
            />
            {!accounts.length ? (
              <button
                type="button"
                className="text-xs font-medium text-brand-blue"
                onClick={() => navigate("/teams")}
              >
                Connect a LinkedIn account
              </button>
            ) : null}
          </div>

          <nav className="mt-4 flex flex-wrap gap-1.5">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  [
                    "rounded-[8px] px-3 py-2 text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-bg-light-blue text-brand-blue"
                      : "text-muted hover:bg-bg-light-blue/60 hover:text-brand-blue",
                  ].join(" ")
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="min-h-0 flex-1 px-4 py-6 sm:px-9 sm:py-6">
        <div className="mx-auto max-w-[1600px]">
          {ownerId ? (
            <Outlet context={outletContext} />
          ) : (
            <div className="rounded-[10px] border border-line bg-white px-6 py-10 text-center">
              <p className="text-sm font-medium text-muted">
                Select or connect a LinkedIn account to continue.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AiContentLayout;
